/**
 * @deprecated v1 implicit $name grammar; authoring stopped 2026-10-22
 *
 * Translates unmarked (grammar 1) stored template text to grammar 2, meaning-preserving:
 * - strict fields (fill, target): v1 read every `$name` as a reference, so the translation is exact;
 * - lenient fields: v1 read `$name` as a reference only when bound at run time. No grammar 2 form says that, so an
 *   embedded name outside the envelope's own scope is kept literal. That scope rule is the heuristic.
 *   Session repairs take no heuristic: display fields translate exactly, any other ambiguous entry is dropped.
 * Pure function of the envelope: every parse site gets the same output for the same input.
 */
import { serialize, TEMPLATE_FIELDS, type Segment, type TemplateField } from './template.ts'
import { mapTemplateTexts } from './template-fields.ts'
import { canonicalJourneyVariableName } from './variable-name.ts'

/** The v1 lexers, verbatim. Only the fill lexer reads `$$` as an escaped `$`. */
const V1_LEXERS = {
  lenient: /\$([A-Za-z_]\w*)/g,
  target: /\$([A-Za-z_]\w*)/g,
  fill: /\$\$|\$([A-Za-z_]\w*)/g,
} as const

export const V1_FIELDS = {
  prose: 'lenient', selector: 'lenient', value: 'lenient', fill: 'fill', target: 'target', argument: 'lenient', toolInit: 'lenient',
} as const satisfies Record<TemplateField, keyof typeof V1_LEXERS>

/** Credential names in scope for every envelope; `mode` included (run-time `$login_mode` handling). */
export const UPGRADE_CREDENTIAL = /^login\d*_(email|password|username|mode)$/

const WHOLE_VALUE = /^\$([A-Za-z_][A-Za-z0-9_]*)$/

/** v1 segments with decoded text: fill `$$` → `$`; elsewhere `$$` is text. */
export function v1Segments(text: string, field: TemplateField): Segment[] {
  const segments: Segment[] = []
  let end = 0
  for (const match of text.matchAll(V1_LEXERS[V1_FIELDS[field]])) {
    segments.push(text.slice(end, match.index))
    segments.push(match[1] === undefined ? '$' : { ref: match[1] })
    end = match.index + match[0].length
  }
  segments.push(text.slice(end))
  return segments.filter(segment => segment !== '')
}

// Fields whose v1 reading of an embedded `$name` grammar 2 states exactly: every reference stays a reference.
type ExactFields = (field: TemplateField) => boolean
const strictV1: ExactFields = field => V1_FIELDS[field] !== 'lenient'
// A display sink shows an unbound reference as `$name` and a bound one as its value, as v1 lenient fields did.
const strictOrDisplay: ExactFields = field => strictV1(field) || TEMPLATE_FIELDS[field] === 'display'

/** Grammar 2 text of v1 `text` in `field`. `scope` decides embedded references of the other (lenient) fields only. */
export function upgradeTemplateV1(text: string, field: TemplateField, scope: ReadonlySet<string>, exactFields = strictV1): string {
  const segments = v1Segments(text, field)
  const whole = segments.length === 1 && typeof segments[0] !== 'string'
  const exact = exactFields(field) || whole
  const kept = exact ? segments : segments.map(s => typeof s === 'string' || inScope(s.ref, scope) ? s : `$${s.ref}`)
  return serialize({ field, segments: kept })
}

function inScope(name: string, scope: ReadonlySet<string>): boolean {
  return scope.has(name) || UPGRADE_CREDENTIAL.test(name)
}

function canonicalName(raw: unknown): string[] {
  const name = typeof raw === 'string' ? canonicalJourneyVariableName(raw) : null
  return name === null ? [] : [name]
}

/** Declared names: `variables[].name`. */
function declaredNames(variables: unknown): string[] {
  return (Array.isArray(variables) ? variables : []).flatMap(v => canonicalName(v && typeof v === 'object' ? (v as { name?: unknown }).name : undefined))
}

/** Store targets: every `variable` key anywhere under `value`. */
function storeTargets(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(storeTargets)
  if (!value || typeof value !== 'object') return []
  return Object.entries(value).flatMap(([key, child]) => key === 'variable' ? canonicalName(child) : storeTargets(child))
}

/** Names used as a whole (trimmed) template value in any template field under `value`. */
function wholeValueNames(value: unknown): string[] {
  const names: string[] = []
  mapTemplateTexts(value, text => {
    const match = WHOLE_VALUE.exec(text.trim())
    if (match) names.push(match[1]!)
    return text
  }, 'all')
  return names
}

function upgradeTexts<T>(value: T, scope: ReadonlySet<string>, exactFields = strictV1): T {
  return mapTemplateTexts(value, (text, field) => upgradeTemplateV1(text, field, scope, exactFields), 'all')
}

type JourneyBlob = { readonly actions?: unknown; readonly variables?: unknown }

/** A journey steps blob's scope: declared ∪ store targets ∪ whole-value names. */
export function journeyScopeV1(blob: JourneyBlob): Set<string> {
  return new Set([...declaredNames(blob.variables), ...storeTargets(blob.actions), ...wholeValueNames(blob.actions)])
}

/** A journey steps blob with its `actions` in grammar 2. `variables`, `contexts` and captured data are not templates. */
export function upgradeJourneyBlobV1<T extends JourneyBlob>(blob: T): T {
  if (!Array.isArray(blob.actions)) return blob
  return { ...blob, actions: upgradeTexts(blob.actions, journeyScopeV1(blob)) }
}

type ToolSteps = { readonly actions: unknown; readonly variables?: unknown }
type ToolRevision = { readonly grammar?: unknown; readonly steps: ToolSteps; readonly projectVariables?: readonly string[] }
type Tool = { readonly parameters?: readonly string[]; readonly revisions: readonly ToolRevision[] }

const unmarked = (revision: { readonly grammar?: unknown }): boolean => revision.grammar === undefined

/** One scope per tool: parameters ∪ per unmarked revision (declared ∪ store targets ∪ whole-value ∪ project variables). */
function toolScopeV1(tool: Tool): Set<string> {
  const names = tool.revisions.filter(unmarked).flatMap(r => [...declaredNames(r.steps.variables),
    ...storeTargets(r.steps.actions), ...wholeValueNames(r.steps), ...(r.projectVariables ?? [])])
  return new Set([...(tool.parameters ?? []), ...names])
}

/** A tool with every unmarked revision's steps in grammar 2; marked revisions are unchanged. */
export function upgradeToolV1<T extends Tool>(tool: T): T {
  const scope = toolScopeV1(tool)
  return { ...tool, revisions: tool.revisions.map(r => unmarked(r) ? { ...r, steps: upgradeTexts(r.steps, scope) } : r) }
}

type SurfaceRevision = { readonly steps: { readonly actions: unknown; readonly variables?: unknown } }

/** A surface revision with its step actions in grammar 2 (scope as a journey blob). */
export function upgradeSurfaceRevisionV1<T extends SurfaceRevision>(revision: T): T {
  return { ...revision, steps: upgradeJourneyBlobV1(revision.steps) }
}

type SessionRepairEntry = { readonly actions: unknown }

export type SessionRepairUpgrade<T> = { kind: 'upgraded'; entry: T } | { kind: 'dropped'; reason: string }

// The first embedded reference outside the entry's scope in a field without an exact translation: v1 read it as a
// run value only if bound, and grammar 2 refuses it unbound.
function ambiguousReference(actions: unknown, scope: ReadonlySet<string>): string | undefined {
  let found: string | undefined
  mapTemplateTexts(actions, (text, field) => {
    const segments = v1Segments(text, field)
    if (found !== undefined || strictOrDisplay(field) || segments.length === 1) return text
    found = segments.flatMap(s => typeof s === 'string' || inScope(s.ref, scope) ? [] : [`$${s.ref} in ${JSON.stringify(text)}`])[0]
    return text
  }, 'all')
  return found
}

/** A session-repair entry in grammar 2, or dropped when a strict-sink lenient field embeds a name no exact translation decides. */
export function upgradeSessionRepairEntryV1<T extends SessionRepairEntry>(entry: T): SessionRepairUpgrade<T> {
  const scope = new Set(wholeValueNames(entry.actions))
  const ambiguous = ambiguousReference(entry.actions, scope)
  if (ambiguous !== undefined) return { kind: 'dropped', reason: `ambiguous v1 reference ${ambiguous}` }
  return { kind: 'upgraded', entry: { ...entry, actions: upgradeTexts(entry.actions, scope, strictOrDisplay) } }
}
